import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Badge } from '../components/ui/badge';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';

const WORK_START = '08:00';
const WORK_END = '19:00';

function timeToMinutes(time) {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function findAvailableSlots(existingServices) {
  const slots = [];
  const workStart = timeToMinutes(WORK_START);
  const workEnd = timeToMinutes(WORK_END);

  // Sort by start time
  const sorted = [...existingServices]
    .filter(s => s.start_time)
    .sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));

  if (sorted.length === 0) {
    slots.push({ start: WORK_START, end: WORK_END, duration: workEnd - workStart });
    return slots;
  }

  // Gap before first service
  const firstStart = timeToMinutes(sorted[0].start_time);
  if (firstStart > workStart + 30) {
    slots.push({ start: WORK_START, end: sorted[0].start_time, duration: firstStart - workStart });
  }

  // Gaps between services
  for (let i = 0; i < sorted.length - 1; i++) {
    const currentEnd = timeToMinutes(sorted[i].end_time || sorted[i].start_time);
    const nextStart = timeToMinutes(sorted[i + 1].start_time);
    if (nextStart - currentEnd >= 30) {
      slots.push({
        start: minutesToTime(currentEnd),
        end: sorted[i + 1].start_time,
        duration: nextStart - currentEnd
      });
    }
  }

  // Gap after last service
  const lastEnd = timeToMinutes(sorted[sorted.length - 1].end_time || sorted[sorted.length - 1].start_time);
  if (workEnd - lastEnd >= 30) {
    slots.push({ start: minutesToTime(lastEnd), end: WORK_END, duration: workEnd - lastEnd });
  }

  return slots;
}

function checkOverlap(startTime, endTime, existingServices) {
  if (!startTime) return null;
  const newStart = timeToMinutes(startTime);
  const newEnd = timeToMinutes(endTime || startTime) || newStart + 60;

  for (const svc of existingServices) {
    if (!svc.start_time) continue;
    const svcStart = timeToMinutes(svc.start_time);
    const svcEnd = timeToMinutes(svc.end_time || svc.start_time) || svcStart + 60;
    if (newStart < svcEnd && newEnd > svcStart) {
      return svc;
    }
  }
  return null;
}

export default function ScheduleRecommendation({ technicianId, date, startTime, endTime, onSelectSlot, excludeServiceId }) {
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!technicianId || !date || technicianId === 'unassigned') {
      setSchedule([]);
      return;
    }

    const fetchSchedule = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ date });
        if (excludeServiceId) params.append('exclude_service_id', excludeServiceId);
        const res = await axios.get(`${API}/services/technician-schedule/${technicianId}?${params}`);
        setSchedule(res.data);
      } catch {
        setSchedule([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSchedule();
  }, [technicianId, date, excludeServiceId]);

  if (!technicianId || technicianId === 'unassigned' || !date) return null;

  const availableSlots = findAvailableSlots(schedule);
  const overlap = checkOverlap(startTime, endTime, schedule);

  return (
    <div className="space-y-2 p-3 bg-muted/30 rounded-lg border border-dashed" data-testid="schedule-recommendation">
      <p className="text-xs font-medium flex items-center gap-1.5 text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        Agenda del técnico este día
      </p>

      {loading ? (
        <p className="text-xs text-muted-foreground">Cargando horarios...</p>
      ) : (
        <>
          {/* Servicios existentes */}
          {schedule.length > 0 ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Servicios programados:</p>
              {schedule.map(svc => (
                <div key={svc.id} className="flex items-center gap-2 text-xs bg-red-50 dark:bg-red-950/20 rounded px-2 py-1 border border-red-200 dark:border-red-800">
                  <span className="font-mono font-bold text-red-600 dark:text-red-400">
                    {svc.start_time || '?'} - {svc.end_time || '?'}
                  </span>
                  <span className="truncate">{svc.client_name}</span>
                  <Badge variant="outline" className="text-xs ml-auto">{svc.folio}</Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckCircle className="h-3 w-3" />
              Sin servicios programados - día libre
            </p>
          )}

          {/* Huecos disponibles */}
          {availableSlots.length > 0 && schedule.length > 0 && (
            <div className="space-y-1 mt-2">
              <p className="text-xs text-muted-foreground">Horarios disponibles sugeridos:</p>
              <div className="flex flex-wrap gap-1">
                {availableSlots.map((slot, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => onSelectSlot(slot.start, slot.end === WORK_END ? minutesToTime(timeToMinutes(slot.start) + 60) : slot.end)}
                    className="text-xs px-2 py-1 rounded bg-green-50 dark:bg-green-950/20 border border-green-300 dark:border-green-700 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors cursor-pointer"
                  >
                    {slot.start} - {slot.end} ({Math.floor(slot.duration / 60)}h{slot.duration % 60 > 0 ? `${slot.duration % 60}m` : ''})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Advertencia de empalme */}
          {overlap && (
            <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-950/20 rounded border border-amber-300 dark:border-amber-700 mt-2" data-testid="overlap-warning">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p className="font-medium text-amber-700 dark:text-amber-300">
                  El horario se empalma con otro servicio
                </p>
                <p className="text-amber-600 dark:text-amber-400">
                  {overlap.folio}: {overlap.start_time} - {overlap.end_time} ({overlap.client_name})
                </p>
                <p className="text-muted-foreground mt-1">
                  Puedes guardar de todas formas, esto es solo una advertencia.
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
